import * as Yup from 'yup';
import { API_BASE_URL, APP_POOL_CONFIG } from '@synergy/config';
import { Box, Stack, styled } from '@mui/material';
import { ErrorIcon } from '@synergy/theme-next';
import { Form, Formik } from 'formik';
import { FormattedMessage } from 'react-intl';
import { PoolButton, PoolFormikPasswordField, PoolTypography } from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components-next';
import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { ErrorText } from '../common/components/error-text/error-text';
import { LoginApi } from '../common/api/login-api';
import { PhoneLink } from '../common/components/phone-link/phone-link';
import Footer from '../common/components/footer/footer';

const Content = styled(Stack)(({ theme }) => ({
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,

    [theme.breakpoints.down('sm')]: {
        paddingTop: theme.spacing(8),
    },
}));

const StyledForm = styled(Form)(({ theme }) => ({
    gap: theme.spacing(3),
    maxWidth: 300,
}));

interface ForgotPasswordData {
    password: string;
    passwordConfirmation: string;
}

export const ResetPassword = (): ReactElement => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [statusCode, setStatusCode] = useState<string | null>(null);

    const newPasswordApi = new LoginApi(API_BASE_URL);

    const initialValues: ForgotPasswordData = {
        password: '',
        passwordConfirmation: '',
    };

    const schema = Yup.object().shape({
        password: Yup.string().required('app.error.required').min(8, 'app.newPassword.charactersRequired'),
        passwordConfirmation: Yup.string()
            .required('app.error.required')
            .min(8, 'app.newPassword.charactersRequired')
            .oneOf([Yup.ref('password'), null], 'app.newPassword.passwordsNotMatching'),
    });

    const handleSubmit = async (values: ForgotPasswordData) => {
        setStatusCode(null);

        if (searchParams.has('token')) {
            const token = searchParams.get('token') ?? '';
            searchParams.delete('token');

            const response = await newPasswordApi.resetPassword(token, values.password);
            if (response.success) {
                navigate({ pathname: '/', search: searchParams.toString() });
            } else {
                setStatusCode('400');
            }
        } else {
            setStatusCode('400');
        }
    };

    const handleCancel = (): void => {
        searchParams.delete('token');
        navigate({ pathname: '/', search: searchParams.toString() });
    };

    return (
        <>
            <Content>
                <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                    <StyledForm>
                        <Stack
                            typography="body1"
                            justifyContent="flex-start"
                            textAlign="center"
                            alignSelf="flex-start"
                            gap={2}
                            sx={{ w: 7.5 }}
                        >
                            <PoolTypography component="div" variant="h4" translationId="app.newPassword" />
                            <Box>
                                <PoolFormattedMessage id="app.newPassword.subtitle" />
                            </Box>
                            <Stack gap={2} sx={{ mt: 2 }}>
                                <PoolFormikPasswordField
                                    name="password"
                                    label={<FormattedMessage id="app.newPassword" />}
                                    fullWidth={true}
                                    required={true}
                                    autoFocus={true}
                                    autoComplete="new-password"
                                />
                                <PoolFormikPasswordField
                                    name="passwordConfirmation"
                                    label={<FormattedMessage id="app.newPassword.passwordConfirm" />}
                                    fullWidth={true}
                                    required={true}
                                    sx={{ mb: 2 }}
                                    autoComplete="new-password"
                                />

                                {statusCode && (
                                    <ErrorText statusCode={statusCode}>
                                        <ErrorIcon />
                                    </ErrorText>
                                )}
                            </Stack>
                            <PoolButton variant="contained" type="submit" fullWidth={true}>
                                <FormattedMessage id="app.newPassword.savePassword" />
                            </PoolButton>
                            <PoolButton onClick={handleCancel} variant="outlined" type="submit" fullWidth={true}>
                                <FormattedMessage id="app.forgotPassword.cancel" />
                            </PoolButton>
                        </Stack>
                    </StyledForm>
                </Formik>
            </Content>
            <Footer>
                <Box>
                    <FormattedMessage
                        id="app.footer.questions"
                        values={{ poolConfigNameShort: APP_POOL_CONFIG['name_short'] }}
                    />{' '}
                    <PhoneLink />
                </Box>
            </Footer>
        </>
    );
};
